import {
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  differenceInMonths,
  differenceInSeconds,
  differenceInYears,
  formatISO,
  formatRFC3339,
} from "date-fns";
import { tz } from "@date-fns/tz";

import { Transfer } from "@prequel/react";

import { DataIntegrityJobSummary, TransferSummary } from "./destination-activities";

type DurationInput = Pick<Transfer | TransferSummary | DataIntegrityJobSummary, "started_at" | "ended_at">;

export const getRelativeSubmittedAt = (submittedAt: string) => {
  if (submittedAt === undefined || submittedAt === "") {
    return "-";
  }

  const utcTime = new Date(submittedAt);
  const now = new Date();

  const seconds = differenceInSeconds(now, utcTime);
  if (seconds < 60) {
    return `${seconds} sec ago`;
  }

  const minutes = differenceInMinutes(now, utcTime);
  if (minutes < 60) {
    return `${minutes} min ago`;
  }

  const hours = differenceInHours(now, utcTime);
  if (hours < 24) {
    return `${hours} hours ago`;
  }

  const days = differenceInDays(now, utcTime);
  if (days < 30) {
    return `${days} days ago`;
  }

  const months = differenceInMonths(now, utcTime);
  if (months < 12) {
    return `${months} months ago`;
  }

  const years = differenceInYears(now, utcTime);
  return `${years} years ago`;
};

export const getDuration = (transfer: DurationInput): string => {
  if (transfer.started_at === undefined) {
    // Return early because there is no time to compute
    return "-";
  }

  const endedAt = transfer.ended_at ?? formatISO(new Date());
  const h = Math.max(differenceInHours(new Date(endedAt), new Date(transfer.started_at)), 0);
  // This gets the total difference in minutes so we % 60 to get the minutes over an hour
  const m = Math.max(differenceInMinutes(new Date(endedAt), new Date(transfer.started_at)) % 60, 0);

  if (h === 0 && m === 0) {
    return "<1m";
  }

  let result = "";
  if (h > 0) {
    result = `${h}h `;
  }

  result += `${m}m`;
  return result;
};

export const formatAsUTC = (val: string) => {
  if (!val) {
    return undefined;
  }

  const utcTime = new Date(val + "Z");
  return formatRFC3339(utcTime, { in: tz("utc") });
};
